import { ReactNode } from "react";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import { useAggregatedFiltersContext } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters.context";
import type { PreviewOriginEntity } from "./cells/ApplyCoaPreviewNameCell";
import {
    ApplyCoaPreviewOriginSource,
    ApplyCoaPreviewSource,
    ApplyCoaPreviewUpdate,
} from "./applyCoaTemplatePreviewStatus";
import {
    getPreviewOriginLabel,
    getPreviewSourceLabel,
    getPreviewUpdatesLabel,
    ORIGIN_TAB,
    SOURCE_TAB,
    UPDATES_TAB,
} from "./applyCoaPreviewFilterOptions";

interface PreviewChip {
    tabKey: string;
    label: ReactNode;
    reset: () => void;
}

interface Props {
    originSources: ApplyCoaPreviewOriginSource[];
    onOriginSourcesChange: (sources: ApplyCoaPreviewOriginSource[]) => void;
    originEntity?: PreviewOriginEntity;
    updates: ApplyCoaPreviewUpdate[];
    onUpdatesChange: (updates: ApplyCoaPreviewUpdate[]) => void;
    source: ApplyCoaPreviewSource;
    onSourceChange: (source: ApplyCoaPreviewSource) => void;
}

// Renders the removable chips + "Clear filters" for the preview's Origin /
// Updates / Source filters, mirroring the shared Filters -> ActiveFilters bar
// used everywhere else. Preview filters are local state (not FiltersTypes), so
// the chips are built here rather than via ActiveFilterButtonLabel. Must render
// inside the AggregatedFiltersProvider so a chip click reopens its popover tab.
export const ApplyCoaPreviewActiveFilters = ({
    originSources,
    onOriginSourcesChange,
    originEntity,
    updates,
    onUpdatesChange,
    source,
    onSourceChange,
}: Props) => {
    const { open } = useAggregatedFiltersContext();

    // Chips show only the selected value(s) — no filter-name prefix — to match
    // the CoA template editor's existing filter chips (e.g. a Source filter
    // renders as "Custom", not "Source: Custom").
    const chips: PreviewChip[] = [
        ...(originSources.length > 0
            ? [
                  {
                      tabKey: ORIGIN_TAB,
                      label: getPreviewOriginLabel(originSources, originEntity),
                      reset: () => onOriginSourcesChange([]),
                  },
              ]
            : []),
        ...(updates.length > 0
            ? [
                  {
                      tabKey: UPDATES_TAB,
                      label: getPreviewUpdatesLabel(updates),
                      reset: () => onUpdatesChange([]),
                  },
              ]
            : []),
        ...(source !== ApplyCoaPreviewSource.all
            ? [
                  {
                      tabKey: SOURCE_TAB,
                      label: getPreviewSourceLabel(source),
                      reset: () => onSourceChange(ApplyCoaPreviewSource.all),
                  },
              ]
            : []),
    ];

    const clearFilters = () => {
        onOriginSourcesChange([]);
        onUpdatesChange([]);
        onSourceChange(ApplyCoaPreviewSource.all);
    };

    return (
        <ActiveFilters
            activeFilters={chips.map((chip) => chip.tabKey)}
            maxVisibleFilters={3}
            renderVisibleFilters={(visibleFilters) =>
                chips
                    .filter((chip) => visibleFilters.includes(chip.tabKey))
                    .map((chip) => (
                        <ActiveFilterButton
                            key={chip.tabKey}
                            label={chip.label}
                            resetFilter={chip.reset}
                            openFilter={() => open(chip.tabKey)}
                        />
                    ))
            }
            clearFilters={clearFilters}
        />
    );
};
